/**
 * Template (home-v3) reveal engine — IntersectionObserver + CSS driven, so it
 * never depends on rAF timing and can never leave content hidden (the final
 * "is-in" state simply sets opacity:1). Reproduces the template's animate
 * attributes:
 *   load-hero-title      → per-word clip rise on load
 *   load-hero-1..N       → staggered fade + slide up on load
 *   fade-up-N            → fade + slide up when scrolled into view
 *   card-stagger         → children fade up in sequence when in view
 *   card-hover           → CSS lift (no JS)
 */
const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function splitWords(el) {
  if (!el || el.dataset.split === "1") return;
  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        const parts = child.textContent.split(/(\s+)/);
        const frag = document.createDocumentFragment();
        parts.forEach((p) => {
          if (p.trim() === "") {
            frag.appendChild(document.createTextNode(p));
          } else {
            const line = document.createElement("span");
            line.className = "tpl-line";
            const w = document.createElement("span");
            w.className = "tpl-word";
            w.textContent = p;
            line.appendChild(w);
            frag.appendChild(line);
          }
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && !child.classList.contains("tpl-line")) {
        walk(child);
      }
    });
  };
  walk(el);
  el.dataset.split = "1";
}

export function initTemplateAnimations(root) {
  const scope = root || document;
  const els = Array.from(scope.querySelectorAll("[data-animate]"));
  const title = scope.querySelector('[data-animate="load-hero-title"]');
  if (title) splitWords(title);

  // Mark ready so CSS applies the hidden pre-state ONLY when JS is running.
  document.documentElement.classList.add("anim-ready");

  if (prefersReduced()) {
    els.forEach((e) => e.classList.add("is-in"));
    return () => {};
  }

  // Stagger index for card-stagger children.
  scope.querySelectorAll('[data-animate="card-stagger"]').forEach((g) => {
    Array.from(g.children).forEach((c, i) => c.style.setProperty("--stagger", i));
  });

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add("is-in");
          io.unobserve(en.target);
        }
      });
    },
    { threshold: 0.08, rootMargin: "0px 0px -5% 0px" },
  );

  els.forEach((e) => {
    const v = e.getAttribute("data-animate") || "";
    if (v.indexOf("load-hero") === 0) {
      // Above the fold on load — reveal on next frame so the transition runs.
      requestAnimationFrame(() => e.classList.add("is-in"));
      return;
    }
    // Pre-pass: if the element is already within (or scrolled past) the
    // viewport at init time, reveal it immediately. IntersectionObserver only
    // fires on FUTURE threshold crossings, so without this an element scrolled
    // past between a prior init and this one would never receive .is-in.
    const r = e.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.95) {
      e.classList.add("is-in");
    } else {
      io.observe(e);
    }
  });

  // Safety net: guarantee any load-hero element is visible even if a frame
  // is skipped.
  const safety = setTimeout(() => {
    scope.querySelectorAll('[data-animate^="load-hero"]').forEach((e) => e.classList.add("is-in"));
  }, 1500);

  return () => {
    clearTimeout(safety);
    io.disconnect();
  };
}
